import {
  Bell,
  BellOff,
  BellRing,
  Check,
  CheckCheck,
  X,
  XCircle,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  GLOBAL_NOTIFICATIONS_REFRESH_EVENT,
  getStoredReadNotificationIds,
  loadGlobalNotifications,
  markNotificationReadOnServer,
  persistReadNotificationIds,
  requestGlobalNotificationsRefresh,
} from "lib/notifications/globalNotifications";
import { respondOrganizationCalendarTaskRequest } from "lib/api/organizationCalendar";
import {
  disableWebPushNotifications,
  enableWebPushNotifications,
  getInitialWebPushState,
  getWebPushBrowserState,
  synchronizeWebPushSubscription,
} from "lib/notifications/webPushSubscriptions";
import { useAppStore } from "lib/store/useAppStore";

function formatNotificationTime(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  return new Intl.DateTimeFormat("ko-KR", {
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function getNotificationType(type) {
  return ["info", "success", "warning", "error"].includes(type) ? type : "info";
}

export default function GlobalNotificationCenter() {
  const navigate = useNavigate();
  const status = useAppStore((state) => state.status);
  const userId = useAppStore((state) => state.user?.id);
  const userRole = useAppStore((state) => state.user?.role);
  const notifications = useAppStore((state) => state.notifications);
  const markNotificationRead = useAppStore(
    (state) => state.markNotificationRead,
  );
  const markAllNotificationsRead = useAppStore(
    (state) => state.markAllNotificationsRead,
  );
  const syncNotifications = useAppStore((state) => state.syncNotifications);
  const clearNotifications = useAppStore((state) => state.clearNotifications);
  const [isOpen, setIsOpen] = useState(false);
  const [rejectionNotificationId, setRejectionNotificationId] = useState("");
  const [rejectionReason, setRejectionReason] = useState("");
  const [respondingScheduleId, setRespondingScheduleId] = useState("");
  const [taskActionError, setTaskActionError] = useState("");
  const [taskActionErrorNotificationId, setTaskActionErrorNotificationId] =
    useState("");
  const [webPushState, setWebPushState] = useState(getInitialWebPushState);
  const [isWebPushChanging, setIsWebPushChanging] = useState(false);
  const [webPushError, setWebPushError] = useState("");
  const [isWebPushPromptDismissed, setIsWebPushPromptDismissed] =
    useState(false);

  const unreadCount = useMemo(
    () =>
      notifications.reduce(
        (count, notification) => count + (notification.read ? 0 : 1),
        0,
      ),
    [notifications],
  );
  const badgeLabel = unreadCount > 99 ? "99+" : String(unreadCount);
  const isWebPushActive =
    webPushState.permission === "granted" &&
    webPushState.subscribed &&
    webPushState.serverRegistered;
  const showWebPushPrompt =
    status === "authenticated" &&
    webPushState.supported &&
    webPushState.serverEnabled !== false &&
    !isWebPushActive &&
    !isWebPushPromptDismissed;
  const webPushDescription = webPushError
    ? webPushError
    : !webPushState.supported
      ? "이 브라우저에서는 브라우저 알림을 사용할 수 없습니다."
      : webPushState.permission === "denied"
        ? "브라우저 설정에서 이 사이트의 알림 권한을 허용해 주세요."
        : webPushState.serverEnabled === false
          ? "브라우저 알림 기능을 현재 사용할 수 없습니다."
          : isWebPushActive
            ? "페이지를 닫아도 브라우저 알림을 받을 수 있습니다."
            : "브라우저 알림을 켜면 페이지를 닫아도 알림을 받을 수 있습니다.";

  const persistReadState = useCallback(
    (notificationIds) => {
      if (!userId) return;
      const readIds = getStoredReadNotificationIds(userId);
      notificationIds.forEach((notificationId) =>
        readIds.add(String(notificationId)),
      );
      persistReadNotificationIds(userId, Array.from(readIds));
    },
    [userId],
  );

  const handleOpenNotification = useCallback(
    (notification) => {
      markNotificationRead(notification.id);
      persistReadState([notification.id]);
      void markNotificationReadOnServer(notification);
      setIsOpen(false);

      if (notification.path) {
        navigate(notification.path);
      }
    },
    [markNotificationRead, navigate, persistReadState],
  );

  const handleMarkAllRead = useCallback(() => {
    const unreadNotifications = notifications.filter(
      (notification) => !notification.read,
    );
    if (unreadNotifications.length === 0) return;

    markAllNotificationsRead();
    persistReadState(
      unreadNotifications.map((notification) => notification.id),
    );
    unreadNotifications.forEach((notification) => {
      void markNotificationReadOnServer(notification);
    });
  }, [markAllNotificationsRead, notifications, persistReadState]);

  const completeTaskRequestAction = useCallback(
    async (notification, action, reason = "") => {
      if (!notification?.scheduleId || respondingScheduleId) return;

      setRespondingScheduleId(notification.scheduleId);
      setTaskActionError("");
      setTaskActionErrorNotificationId("");
      const result = await respondOrganizationCalendarTaskRequest(
        notification.scheduleId,
        {
          action,
          rejectionReason: reason,
        },
      );
      setRespondingScheduleId("");

      if (!result.success) {
        setTaskActionError(
          result.message || "업무 요청을 처리하지 못했습니다.",
        );
        setTaskActionErrorNotificationId(notification.id);
        return;
      }

      markNotificationRead(notification.id);
      persistReadState([notification.id]);
      setRejectionNotificationId("");
      setRejectionReason("");
      setTaskActionErrorNotificationId("");
      requestGlobalNotificationsRefresh();
    },
    [markNotificationRead, persistReadState, respondingScheduleId],
  );

  const handleRejectTaskRequest = useCallback(
    (event, notification) => {
      event.preventDefault();
      const reason = rejectionReason.trim();
      if (!reason) {
        setTaskActionError("반려 이유를 입력해 주세요.");
        setTaskActionErrorNotificationId(notification.id);
        return;
      }

      void completeTaskRequestAction(notification, "reject", reason);
    },
    [completeTaskRequestAction, rejectionReason],
  );

  useEffect(() => {
    if (status !== "authenticated" || !userId) {
      clearNotifications();
      setIsOpen(false);
      return undefined;
    }

    let isActive = true;
    clearNotifications();

    const refreshNotifications = async () => {
      const result = await loadGlobalNotifications({
        userId,
        role: userRole,
      });
      if (!isActive) return;
      syncNotifications(result.notifications, result.synchronizedSources);
    };

    void refreshNotifications();
    const intervalId = window.setInterval(refreshNotifications, 60_000);
    window.addEventListener("focus", refreshNotifications);
    window.addEventListener(
      GLOBAL_NOTIFICATIONS_REFRESH_EVENT,
      refreshNotifications,
    );

    return () => {
      isActive = false;
      window.clearInterval(intervalId);
      window.removeEventListener("focus", refreshNotifications);
      window.removeEventListener(
        GLOBAL_NOTIFICATIONS_REFRESH_EVENT,
        refreshNotifications,
      );
    };
  }, [clearNotifications, status, syncNotifications, userId, userRole]);

  useEffect(() => {
    let isActive = true;

    if (status !== "authenticated" || !userId) {
      setWebPushState(getInitialWebPushState());
      setWebPushError("");
      return undefined;
    }

    const synchronizeSubscription = async () => {
      const nextState = await synchronizeWebPushSubscription();
      if (isActive) setWebPushState(nextState);
    };

    void synchronizeSubscription();
    const handleWindowFocus = () => void synchronizeSubscription();
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        void synchronizeSubscription();
      }
    };
    window.addEventListener("focus", handleWindowFocus);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      isActive = false;
      window.removeEventListener("focus", handleWindowFocus);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [status, userId]);

  useEffect(() => {
    setIsWebPushPromptDismissed(false);
  }, [userId]);

  const handleToggleWebPush = useCallback(async () => {
    if (isWebPushChanging) return;

    setIsWebPushChanging(true);
    setWebPushError("");
    try {
      const nextState = isWebPushActive
        ? await disableWebPushNotifications()
        : await enableWebPushNotifications();
      setWebPushState(nextState);
    } catch (error) {
      setWebPushError(
        error?.message || "브라우저 알림 설정을 변경하지 못했습니다.",
      );
      const nextState = await getWebPushBrowserState().catch(() => null);
      if (nextState) setWebPushState(nextState);
    } finally {
      setIsWebPushChanging(false);
    }
  }, [isWebPushActive, isWebPushChanging]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") setIsOpen(false);
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen]);

  return (
    <div className="globalNotificationCenter">
      {showWebPushPrompt && (
        <section
          aria-labelledby="web-push-permission-title"
          className="globalNotificationCenter__pushPrompt"
        >
          <span aria-hidden="true">
            <BellRing size={20} strokeWidth={2} />
          </span>
          <div>
            <strong id="web-push-permission-title">
              {webPushState.permission === "denied"
                ? "브라우저 알림이 차단되어 있습니다."
                : "페이지를 닫아도 새 알림을 받아보세요."}
            </strong>
            <p>
              {webPushState.permission === "denied"
                ? "주소창 왼쪽의 사이트 정보 아이콘에서 알림 권한을 허용한 뒤 이 페이지로 돌아오세요."
                : "업무 요청과 댓글이 등록되면 Windows 또는 브라우저 알림으로 알려드립니다."}
            </p>
          </div>
          <div className="globalNotificationCenter__pushPromptActions">
            {webPushState.permission !== "denied" && (
              <button
                disabled={isWebPushChanging}
                onClick={handleToggleWebPush}
                type="button"
              >
                {isWebPushChanging ? "처리 중" : "알림 허용"}
              </button>
            )}
            <button
              className="is-secondary"
              onClick={() => setIsWebPushPromptDismissed(true)}
              type="button"
            >
              {webPushState.permission === "denied" ? "확인" : "나중에"}
            </button>
          </div>
        </section>
      )}

      <button
        aria-controls="global-notification-panel"
        aria-expanded={isOpen}
        aria-label={
          unreadCount > 0
            ? `알림 열기, 미확인 알림 ${unreadCount}개`
            : "알림 열기"
        }
        className={`globalNotificationCenter__trigger${
          unreadCount > 0 ? " has-notifications" : ""
        }`}
        onClick={() => setIsOpen((current) => !current)}
        type="button"
      >
        <Bell aria-hidden="true" size={24} strokeWidth={2} />
        {unreadCount > 0 && (
          <span
            aria-label={`미확인 ${unreadCount}개`}
            className="globalNotificationCenter__badge"
          >
            {badgeLabel}
          </span>
        )}
      </button>

      <button
        aria-hidden={!isOpen}
        aria-label="알림 패널 닫기"
        className={`globalNotificationCenter__backdrop${
          isOpen ? " is-open" : ""
        }`}
        onClick={() => setIsOpen(false)}
        tabIndex={-1}
        type="button"
      />

      <aside
        aria-hidden={!isOpen}
        aria-labelledby="global-notification-title"
        aria-modal="true"
        className={`globalNotificationCenter__panel${isOpen ? " is-open" : ""}`}
        id="global-notification-panel"
        inert={isOpen ? undefined : ""}
        role="dialog"
      >
        <header className="globalNotificationCenter__header">
          <div>
            <h2 id="global-notification-title">알림</h2>
            <p>
              {unreadCount > 0
                ? `확인이 필요한 알림 ${unreadCount}개`
                : "새로 확인할 알림이 없습니다."}
            </p>
          </div>
          <button
            aria-label="알림 패널 닫기"
            className="globalNotificationCenter__close"
            onClick={() => setIsOpen(false)}
            type="button"
          >
            <X aria-hidden="true" size={20} />
          </button>
        </header>

        <div className="globalNotificationCenter__toolbar">
          <div className="globalNotificationCenter__toolbarSummary">
            <strong>전체 알림 {notifications.length}개</strong>
            {status === "authenticated" && (
              <span className={webPushError ? "is-error" : ""}>
                {webPushDescription}
              </span>
            )}
          </div>
          <div className="globalNotificationCenter__toolbarActions">
            {status === "authenticated" && (
              <button
                className={isWebPushActive ? "is-push-active" : ""}
                disabled={
                  isWebPushChanging ||
                  !webPushState.supported ||
                  webPushState.permission === "denied"
                }
                onClick={handleToggleWebPush}
                type="button"
              >
                {isWebPushActive ? (
                  <BellOff aria-hidden="true" size={15} />
                ) : (
                  <BellRing aria-hidden="true" size={15} />
                )}
                {isWebPushChanging
                  ? "처리 중"
                  : isWebPushActive
                    ? "브라우저 알림 끄기"
                    : webPushState.permission === "denied"
                      ? "브라우저에서 차단됨"
                      : "브라우저 알림 켜기"}
              </button>
            )}
            <button
              disabled={unreadCount === 0}
              onClick={handleMarkAllRead}
              type="button"
            >
              <CheckCheck aria-hidden="true" size={15} />
              모두 확인
            </button>
          </div>
        </div>

        <div className="globalNotificationCenter__body">
          {notifications.length > 0 ? (
            <ul className="globalNotificationCenter__list">
              {notifications.map((notification) => {
                const type = getNotificationType(notification.type);
                const isRejecting = rejectionNotificationId === notification.id;
                const isResponding =
                  respondingScheduleId === notification.scheduleId;

                return (
                  <li key={notification.id}>
                    <button
                      className={[
                        "globalNotificationCenter__item",
                        `is-${type}`,
                        notification.read ? "" : "is-unread",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      onClick={() => handleOpenNotification(notification)}
                      type="button"
                    >
                      <span
                        aria-hidden="true"
                        className="globalNotificationCenter__itemIcon"
                      >
                        <Bell size={16} strokeWidth={2.1} />
                      </span>
                      <span className="globalNotificationCenter__itemContent">
                        <span className="globalNotificationCenter__itemTopline">
                          <strong>{notification.title}</strong>
                          {!notification.read && <small>확인 필요</small>}
                        </span>
                        {notification.message && <p>{notification.message}</p>}
                        <span className="globalNotificationCenter__itemTime">
                          {formatNotificationTime(notification.createdAt)}
                        </span>
                      </span>
                    </button>
                    {notification.actionRequired && (
                      <div className="globalNotificationCenter__taskActions">
                        {isRejecting ? (
                          <form
                            onSubmit={(event) =>
                              handleRejectTaskRequest(event, notification)
                            }
                          >
                            <label
                              htmlFor={`task-rejection-${notification.id}`}
                            >
                              반려 이유
                            </label>
                            <textarea
                              autoFocus
                              disabled={isResponding}
                              id={`task-rejection-${notification.id}`}
                              maxLength={500}
                              onChange={(event) => {
                                setRejectionReason(event.target.value);
                                setTaskActionError("");
                              }}
                              placeholder="요청자에게 전달할 반려 이유를 입력하세요."
                              value={rejectionReason}
                            />
                            {taskActionError && (
                              <p role="alert">{taskActionError}</p>
                            )}
                            <div>
                              <button
                                disabled={isResponding}
                                onClick={() => {
                                  setRejectionNotificationId("");
                                  setRejectionReason("");
                                  setTaskActionError("");
                                  setTaskActionErrorNotificationId("");
                                }}
                                type="button"
                              >
                                취소
                              </button>
                              <button disabled={isResponding} type="submit">
                                {isResponding ? "처리 중" : "반려 사유 보내기"}
                              </button>
                            </div>
                          </form>
                        ) : (
                          <>
                            <button
                              className="is-accept"
                              disabled={isResponding}
                              onClick={() =>
                                void completeTaskRequestAction(
                                  notification,
                                  "accept",
                                )
                              }
                              type="button"
                            >
                              <Check aria-hidden="true" size={14} />
                              {isResponding ? "처리 중" : "수락"}
                            </button>
                            <button
                              className="is-reject"
                              disabled={isResponding}
                              onClick={() => {
                                setRejectionNotificationId(notification.id);
                                setRejectionReason("");
                                setTaskActionError("");
                                setTaskActionErrorNotificationId("");
                              }}
                              type="button"
                            >
                              <XCircle aria-hidden="true" size={14} />
                              반려
                            </button>
                          </>
                        )}
                        {!isRejecting &&
                          taskActionError &&
                          taskActionErrorNotificationId === notification.id && (
                            <p role="alert">{taskActionError}</p>
                          )}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          ) : (
            <div className="globalNotificationCenter__empty">
              <span aria-hidden="true">
                <Bell size={28} strokeWidth={1.7} />
              </span>
              <strong>알림이 없습니다.</strong>
              <p>새 알림이 도착하면 이곳에 표시됩니다.</p>
            </div>
          )}
        </div>
      </aside>
    </div>
  );
}
